import {WorkerMessage} from '../worker-message.interface';
import {MessageBusService} from '../message-bus.service';
import {Injectable, NgZone} from '@angular/core';
import {
    EventStreamConnectionChangedUIMessage
} from '../ui-messages/event-stream-connection-changed-message-ui-message.interface';
import {SessionService} from '../authentication/session-service.service';
import {EtsSessionData} from 'projects/shared-components/webworkers/ets-session-data.interface';
import {environment} from '../environments/environment';
import {Logger} from '../logging/logger.interface';
import {EtsWorker} from '../webworkers/ets-worker.class';
import {WorkerLoadedUIMessage} from '../ui-messages/worker-loaded-ui-message.interface';
import {CrossThreadMessage} from '../webworkers/cross-thread-message.interface';
import {AuthTokenExpiredUIMessage} from '../ui-messages/auth-token-expired-ui-message.interface';
import {LoggerService} from '../logging/logger-factory.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {QuoteDto} from "../shell-communication/dtos/quote-dto.class";
import { EssConnectionDiagnosticsService } from './ess-connection-diagnostics.service';
import { EssDiagnosticEvent } from './ess-diagnostic-event';

@Injectable({providedIn: 'root'})
export class EventStreamServiceClient {
    constructor(
        private _messageBus: MessageBusService,
        private _sessionService: SessionService,
        private readonly _ngZone: NgZone,
        loggerService: LoggerService,
        private readonly diagnostics: EssConnectionDiagnosticsService
    ) {
        this._logger = loggerService.createLogger('EventStreamServiceClient');
    }

    private _logger: Logger;
    private _isConnected: boolean;
    private _essWorker: EtsWorker;
    private _unsubscriber: Subject<any>;
    private _resolve: (value?: (PromiseLike<unknown> | unknown)) => void;

    // CR-20260511-001 — retired iter 25 main-thread hidden-buffer.
    //
    // Background: CR-20260508-001 iter 25 added a main-thread buffer
    // that held all non-LIVENESS messages while `document.hidden`, then
    // drained them in rAF chunks on tab return. Goal was to prevent
    // post-return DOM-mutation→repaint storm.
    //
    // Why removed: CR-20260508-001 iter 28 introduced a stronger
    // worker-side hidden-coalesce that absorbs WS frames into
    // ThrottlerCache (FreshOnly+Batch) upstream — main thread never sees
    // them during hide. With iter 28 in place, this main-thread buffer
    // was a redundant defense layer that never carried real load on
    // /app, and on /init it deadlocked initialization when a service
    // awaited a buffered QuoteDto.
    //
    // Sole remaining defense layer: worker-side hidden-coalesce, now
    // gated on `pauseResumeGateEnabled` (see ess-worker.ts) so it
    // engages only on /app. Off-/app hides dispatch live.

    get isConnected(): boolean {
        return this._isConnected;
    }

    init(): Promise<any> {

        const start = Date.now();

        if (!!this._unsubscriber) {
            this._unsubscriber.next();
            this._unsubscriber.complete();
        }

        this._unsubscriber = new Subject<any>();

        const prms = new Promise((resolve, reject) => {
            this._resolve = resolve;
        });

        this._loadWorker().then(() => {

            this._sendSessionDataToWorker();
            this._attachVisibilityForwarder();

            this._messageBus
                .of<AuthTokenExpiredUIMessage>('AuthTokenExpiredUIMessage')
                .pipe(takeUntil(this._unsubscriber))
                .subscribe(msg => setTimeout(() => this._onAuthTokenExpiredUIMessage(msg.payload), 0));

            this._messageBus
                .of<EventStreamConnectionChangedUIMessage>('EventStreamConnectionChangedUIMessage')
                .pipe(takeUntil(this._unsubscriber))
                .subscribe(x => {
                    this._isConnected = x.payload.isConnected;
                    if (x.payload.isConnected) {
                        if (this._resolve) {
                            this._resolve();
                            this._resolve = null;
                        }

                        const end = Date.now();

                        this._messageBus.publish({
                            topic: 'ServiceInitialized',
                            payload: {
                                time: end - start,
                                name: 'EssClient'
                            }
                        });

                        // if (this._resolve) {
                        //     this._resolve(x.payload);
                        //     this._resolve = null;
                        // }
                    }
                });
        });

        return prms;
    }

    private _sendSessionDataToWorker(): void {
        this._logger.info('Preparing session data for ess-worker');

        const authToken = this._sessionService.authToken;

        const shells = this._sessionService.sessionData.availableShells
            ? this._sessionService.sessionData.availableShells
            : [];

        const connectedShell = this._sessionService.connectedShell ? this._sessionService.connectedShell : null;

        const payload: EtsSessionData = {
            authToken,
            shells,
            connectedShell,
            applicationId: environment.runtimeAppId,
            eventStreamUrl: environment.eventStreamUrl,
            diagnosticSessionId: this.diagnostics.beginSession(authToken),
            diagnosticAppVersion: environment.productVersion
        };

        const message: WorkerMessage = {
            payload,
            messageType: 'session-data'
        };

        this._logger.info('Sending session data for ess-worker', message);

        this._sendMessageToWorker(message);
    }

    private _loadWorker(): Promise<void> {
        if (this._essWorker) {
            this._logger.info(`ESS worker already loaded. Skipping.`);
            return Promise.resolve(null);
        }

        const workerPath = `ess-worker.bundle.js`;
        const worker = new EtsWorker(workerPath);

        // to access service's context in event handler, where 'this' will be overriden
        const self = this;

        this._ngZone.runOutsideAngular(() => {
            worker.onerror = (error: ErrorEvent) => {
                self._workerOnError(error);
            };

            worker.onmessage = (message: MessageEvent) => {
                self._workerOnMessage(message);
            };
        });

        this._essWorker = worker;

        const promise = new Promise((res, rej) => {
            let timeout;
            const subs = this._messageBus.of<WorkerLoadedUIMessage>('WorkerLoadedUIMessage').subscribe(msg => {
                subs.unsubscribe();

                res({});

                if (timeout) {
                    clearTimeout(timeout);
                }
            });

            timeout = setTimeout(() => {
                rej('Load timeout');
                subs.unsubscribe();
            }, 7 * 1000);
        });

        return promise as any;
    }

    private _sendMessageToWorker(message: WorkerMessage): void {
        if (!this._essWorker) {
            throw new Error('Worker not loaded');
        }

        this._essWorker.postMessage(message);
    }

    // CR-20260424-003 — forward window-active state to the worker. Polling
    // architecture (1 Hz) — does not depend on browser firing
    // visibilitychange/blur/focus events, which empirically don't fire for
    // every "user moved away" action in all environments. Reads
    // document.hasFocus() and document.hidden directly each second; only
    // sends to worker on transitions.
    private _visibilityHandlerAttached = false;
    private _attachVisibilityForwarder(): void {
        if (this._visibilityHandlerAttached || typeof document === 'undefined') {
            return;
        }
        this._visibilityHandlerAttached = true;
        // LEAK-1 hotfix — visibility = document.hidden only. Dropping
        // hasFocus() — DevTools focus / other-OS-window focus must NOT
        // mark tab inactive (page is still visible to user, server-side
        // Pause shouldn't fire). Matches SessionSyncOverlayService.computeIsActive.
        const computeActive = () => !document.hidden;
        let lastSent: boolean | undefined = undefined;
        const sendIfChanged = () => {
            if (!this._essWorker) {
                return;
            }
            const nowActive = computeActive();
            if (nowActive === lastSent) {
                return;
            }
            lastSent = nowActive;
            this._essWorker.postMessage({
                messageType: 'UI_LAG_FOCUS_STATE',
                payload: { isVisible: nowActive }
            });
        };
        // Initial state.
        sendIfChanged();
        // Poll every 1 s. Same cadence as the main-thread service tick.
        setInterval(sendIfChanged, 1000);

        // ALSO listen to visibilitychange directly — fires faster than
        // polling. CR-20260424-003 polling is the safety net.
        // CR-20260508-001 iter 27 — also push the visibility state to the
        // worker on EVERY transition (both hide and show), so the worker
        // engages/flushes its hidden-coalesce in the same task as the
        // main-thread transition. Polling at 1Hz could otherwise leave a
        // ~1s window where main is hidden but worker doesn't know yet
        // and keeps posting raw messages.
        document.addEventListener('visibilitychange', () => {
            const nowActive = computeActive();
            if (nowActive === lastSent) {
                return;
            }
            lastSent = nowActive;
            if (this._essWorker) {
                this._essWorker.postMessage({
                    messageType: 'UI_LAG_FOCUS_STATE',
                    payload: { isVisible: nowActive }
                });
            }
        });
    }

    // CR-20260511-001 — kept as documented inert constant per user
    // direction. After iter 25 main-thread hidden-buffer removal there
    // are no callers; left in place for reference. Liveness/lifecycle
    // types are now handled either by worker-direct postToGui
    // (WorkerLoaded/EventStreamConnectionChanged/AuthTokenExpired) or by
    // dedicated branches in _workerOnMessage (UI_LAG_PING).
    private static readonly LIVENESS_MESSAGE_TYPES = new Set<string>([
        'UI_LAG_PING',
        'UI_LAG_PONG',
        'EventStreamConnectionChangedUIMessage',
        'AuthTokenExpiredUIMessage',
        'WorkerLoadedUIMessage',
        'ServerTimeMessageDto',  // session-sync overlay probe A signal — must keep flowing
    ]);

    private _workerOnMessage(message: MessageEvent): void {
        const envelope = message.data as CrossThreadMessage;
        if (envelope.messageType === 'EssConnectionDiagnostic') {
            this.diagnostics.record(envelope.payload as EssDiagnosticEvent);
            return;
        }

        // UI_LAG_PING is always immediate — worker liveness signal.
        if (envelope.messageType === 'UI_LAG_PING') {
            const { id } = envelope.payload as { id: number };
            this._essWorker.postMessage({
                messageType: 'UI_LAG_PONG',
                payload: { id }
            });
            return;
        }

        if (envelope.messageType === '[]') {
            const messages = envelope.payload as CrossThreadMessage[];
            this._dispatchBatchImmediate(messages);
            return;
        }

        this._messageBus.publishAsync({
            topic: envelope.messageType,
            payload: envelope.payload
        });
    }

    private _dispatchBatchImmediate(messages: CrossThreadMessage[]): void {
        for (const msg of messages) {
            this._dispatchSingle(msg);
        }
    }

    private _dispatchSingle(msg: CrossThreadMessage): void {
        if (msg.messageType === 'QuoteDto') {
            const arr = msg.payload as QuoteDto[];
            if (arr['length']) {
                arr.forEach((item) => {
                    if ((item.lastPx + item.bid + item.ask) === 0) {
                        item.lastPx = item.bid = item.ask = item.mid = null;
                    }
                });
            }
        }

        // CR-20260508-001 iter 33 — unwrap throttler's array payload for
        // ServerTimeMessageDto. Iter 29 added ServerTimeMessageDto to the
        // FreshOnly throttler with a 'singleton' cache key (to suppress
        // post-disconnect server-clock-catchup flood). Throttler.getBatches()
        // unconditionally produces array-shape payload (Object.keys.map)
        // for FreshOnly storage. All ServerTimeMessageDto consumers
        // (server-time-panel, session-sync-overlay, etc.) expect a single
        // object — `payload.time` access on an array returns undefined,
        // which is the bug user observed: time field "missing" in JSON.
        // Unwrap here, in the dispatcher, so all consumers see the
        // expected shape.
        let dispatchPayload: any = msg.payload;
        if (msg.messageType === 'ServerTimeMessageDto' && Array.isArray(dispatchPayload)) {
            const arr = dispatchPayload as any[];
            dispatchPayload = arr.length > 0 ? arr[arr.length - 1] : null;
        }

        // CR-20260509-005 Phase-6 — VerticalSkew / SkewSentiment / QuoteLimit
        // were added to the FreshOnly throttler in iter 34 of this same CR-001
        // branch. ThrottlerCache.getBatches → _processMapStorage emits their
        // payload as an array (`Object.keys(cache).map(...)`), one element per
        // cache key. Consumers of these topics read single-object fields
        // (dto.underlying / dto.value / etc.); on an array those fields read
        // as undefined and every message is silently filter-rejected. Fan the
        // array out — one publish per element preserves the pre-iter-34
        // single-DTO consumer contract.
        // 2026-06-10 — CollarsPositionChangedDto joined the FreshOnly
        // throttler (per-positionId last-wins); its array payload carries one
        // element per position and the Collars panel consumes single DTOs,
        // so it takes the same fan-out as the iter-34 types.
        // CR-20260619-001 (Hedging Grid CoPilot) — CoPilotSolutionsReadyDto rides the BATCH
        // throttler, so its payload arrives here as an array (getBatches → _processArrayStorage).
        // The CoPilot child consumes ONE DTO per bus event (correlated by runId), so fan the array
        // out to single-DTO publishes like the other listed types.
        // CR-20260716-001 (OPG CoPilot skill) — OpgCoPilotTradesReadyDto rides the same BATCH
        // lane; its consumer (BackendOpgTradesSkill) likewise reads ONE DTO per bus event
        // (pending-run map correlated by runId), so it takes the same fan-out.
        // CR-20260910-001 (Maven) — the last of the THREE routing sites, all four new DTOs
        // registered in ONE pass by the foundation pass so three lanes never edit this file at
        // once. Every one of them rides the BATCH throttler and every consumer reads ONE DTO per
        // bus event correlated by runId, so they take the same fan-out:
        //   ApgOrionCardsReadyDto    the migrated roll's delivery (milestone 1)
        //   HmCloseSolutionsReadyDto the Close's own delivery (milestone 2) — deliberately NOT
        //                            CoPilotSolutionsReadyDto: a Close never displaces an Adjust
        //   MavenResultReadyDto      the ONE delivery per Maven run (milestone 3)
        //   MavenRunStageDto         stage events, SEVERAL per run and not a delivery; the panel
        //                            shows them as they arrive and reveals cards once
        // CR-20260930-001 (Portfolio Hedging Analysis session) — the analysis-session push rides the
        // BATCH lane too (throttler registration), so its payload arrives here as an array; BOTH
        // consumers read ONE DTO per bus event (the analysis session service gates it on sessionId/
        // comboId, the Collars panel on reason), so it takes the same fan-out. Without it every push
        // reached the panels with an array payload and was silently dropped (the analysis panel never
        // re-read the session — exported blocks stayed "awaiting fill" after they were acquired).
        if (Array.isArray(dispatchPayload) &&
            (msg.messageType === 'VerticalSkewChangedDto'
                || msg.messageType === 'SkewSentimentChangedDto'
                || msg.messageType === 'HorizontalSkewChangedDto'
                || msg.messageType === 'QuoteLimitDto'
                || msg.messageType === 'CollarsPositionChangedDto'
                || msg.messageType === 'ActionBoardChangedDto'
                || msg.messageType === 'CoPilotSolutionsReadyDto'
                || msg.messageType === 'OpgCoPilotTradesReadyDto'
                || msg.messageType === 'ApgOrionCardsReadyDto'
                || msg.messageType === 'HmCloseSolutionsReadyDto'
                || msg.messageType === 'AdjustmentPreviewReadyDto'
                || msg.messageType === 'MavenResultReadyDto'
                || msg.messageType === 'MavenRunStageDto'
                || msg.messageType === 'AnalysisSessionChangedDto')) {
            for (const item of dispatchPayload as any[]) {
                this._messageBus.publishAsync({
                    topic: msg.messageType,
                    payload: item
                });
            }
            return;
        }

        this._messageBus.publishAsync({
            topic: msg.messageType,
            payload: dispatchPayload
        });
    }

    private _workerOnError(error: ErrorEvent): void {
        this._logger.error('Worker error', error);
    }

    private _onAuthTokenExpiredUIMessage(msg: AuthTokenExpiredUIMessage): void {
        this._logger.info('Received message that auth. token is expired', msg);

        if (!this._essWorker) {
            this._logger.warn('"AuthTokenExpiredUIMessage" cannot be delivered to ESS worker. It is not loaded');
        }

        this._essWorker.postMessage({
            messageType: 'disconnect',
            payload: {shouldReconnect: false}
        });
    }

    // CR-20260509-001 — gate pause/resume signaling on /app-route presence.
    // Caller (WebtraderHostComponent) flips true on ngOnInit (mounts only on
    // /app per wt-app-routing.module) and false on ngOnDestroy. Worker uses
    // this gate to decide whether visibilitychange should send Pause/Resume
    // to ESS. Outside /app (login/init/maintenance/logout), pause/resume
    // signaling is suppressed; backend treats client as fully active.
    public setPauseResumeGate(enabled: boolean): void {
        if (!this._essWorker) {
            return;
        }
        this._essWorker.postMessage({
            messageType: 'PAUSE_RESUME_GATE',
            payload: { enabled }
        });
    }
}
